import { ReactNode, useEffect, useRef } from "react";
import "./InfiniteScroll.scss";
import { useScrollStore } from "@/common/store/scrollStore";

type IProps = {
  fetchMore: () => void;
  hasMore: boolean;
  root?: string;
  children: ReactNode;
};

const THRESHOLD = 0.01;

export const InfiniteScroll = ({ fetchMore, hasMore, root, children }: IProps) => {
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const spyElement = useRef<HTMLDivElement>(null);
  const lastBoundingClientRect = useRef<DOMRectReadOnly>(undefined);
  const observer = useRef<IntersectionObserver | null>(null);
  const handleNext = useRef<() => void>(fetchMore);

  const startObserve = () => {
    if (spyElement.current === null || !observer.current) {
      return;
    }

    observer.current.observe(spyElement.current);
  };

  const clearObserve = () => {
    observer.current?.disconnect();
  };

  useEffect(() => {
    const callback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (lastBoundingClientRect.current === entry.boundingClientRect) {
            return;
          }

          lastBoundingClientRect.current = entry.boundingClientRect;
          handleNext.current();
        }
      });
    };

    const resolvedRoot = root ? document.querySelector(root) : scrollElement;

    observer.current = new IntersectionObserver(callback, {
      root: resolvedRoot,
      rootMargin: "100% 0% 100% 0%",
      threshold: THRESHOLD,
    });

    if (hasMore) {
      startObserve();
    }

    return () => {
      clearObserve();
      observer.current = null;
    };
  }, [root]);

  useEffect(() => {
    handleNext.current = fetchMore;
  }, [fetchMore]);

  useEffect(() => {
    hasMore ? startObserve() : clearObserve();
  }, [hasMore]);

  return (
    <>
      {children}
      <div className="infiniteScroll" ref={spyElement} key="spyElement"></div>
    </>
  );
};
